/**
 * API client for Lemon Squeezy checkout and subscription endpoints.
 *
 * These call our own server which acts as a proxy to Lemon Squeezy —
 * the LS API key never leaves the backend.
 *
 * Request plumbing lives in httpClient.ts, which sends the httpOnly session
 * cookie automatically — no token parameter required.
 */
import { postJson, getJson } from './httpClient';
import { isErrorResponse } from '../utils/apiUtils';

/** Response shape for the checkout endpoint. */
export interface CheckoutResponse {
    checkoutUrl?: string;
    error?: string;
}

/** Response shape for the subscription status endpoint. */
export interface SubscriptionResponse {
    plan: 'free' | 'pro' | 'teams';
    /** The user's category, which may escalate the effective access tier. */
    userCategory?: 'standard_user' | 'power_user' | 'zeytune_team';
    subscriptionStatus: string | null;
    subscriptionRenewsAt: string | null;
    lemonSqueezyCustomerId: string | null;
    error?: string;
}

/** Subscription fields a caller can rely on when the request fails. */
const UNKNOWN_SUBSCRIPTION = {
    plan: 'free',
    subscriptionStatus: null,
    subscriptionRenewsAt: null,
    lemonSqueezyCustomerId: null,
} as const;

/**
 * Creates a Lemon Squeezy checkout session for the Pro plan.
 * Returns a checkout URL that the user should be redirected/navigated to.
 */
export async function createCheckoutSession(): Promise<CheckoutResponse> {
    return postJson<CheckoutResponse>('/api/checkout', undefined, {
        networkErrorMessage: (e) => (e as Error)?.message || 'Network error',
    });
}

/**
 * Fetches the current user's subscription status from the server.
 *
 * Callers read `plan` unconditionally, so a failure is widened back into a full
 * free-plan response carrying the error rather than a bare `{ error }`.
 */
export async function fetchSubscriptionStatus(): Promise<SubscriptionResponse> {
    const result = await getJson<SubscriptionResponse>('/api/subscription', {
        networkErrorMessage: (e) => (e as Error)?.message || 'Network error',
    });
    return isErrorResponse(result) ? { ...UNKNOWN_SUBSCRIPTION, error: result.error } : result;
}
